import { MouseEventHandler, useEffect, useRef } from 'react'
import { useController, useFormContext } from 'react-hook-form'
import toast from 'react-hot-toast'
import { MutationFunction, useMutation } from 'react-query'
import SignatureCanvas from 'react-signature-canvas'
import { Box, Button, FormControl, FormHelperText, InputLabel, SxProps } from '@mui/material'

import { DefaultElementProps, File as FileObj } from 'common/interfaces'
import { cancelBtnStyle } from 'common/styles'
import { SizingContainer } from 'components/SizingContainer'
import { useBoolean, useIsOnline } from 'utils/hook'
import { getFileExtensionAndUniqName, useUploadFileToAWS } from 'utils/uploadFileToS3'
import { validationsToMapRules } from 'utils/validationsToRules'

import { Signature as SignatureProps } from './interface'

interface UrlToFileVariable {
  url: string
  filename: string
  mimeType: string
}

const style: Record<string, SxProps> = {
  labelStyle: {
    transform: 'translate(14px, -9px) scale(0.75)',
    background: 'white',
    paddingX: '5px',
  },
  canvasWrapper: {
    height: '200px',
    marginBottom: '8px',
    border: '1px solid rgba(0, 0, 0, 0.23)',
    borderRadius: '4px',
  },
}

export default function Signature({
  name,
  label,
  size,
  validations,
}: SignatureProps & DefaultElementProps): JSX.Element {
  const { labelStyle, canvasWrapper } = style

  const isOnline = useIsOnline()
  const { mutate: sendFileToAWS } = useUploadFileToAWS()

  const { control, setError } = useFormContext()
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
    rules: validationsToMapRules(validations),
  })

  const [isLoading, setLoading, setLoaded] = useBoolean(false)

  const signatureCanvasRef = useRef<SignatureCanvas>(null)

  const clearCanvas: MouseEventHandler = () => {
    signatureCanvasRef.current?.clear()
    field.onChange({})
  }

  const urlToFile: MutationFunction<File, UrlToFileVariable> = async ({
    url,
    filename,
    mimeType,
  }) => {
    const fetchUrl = await fetch(url)
    const arrayBufferResult = await fetchUrl.arrayBuffer()
    return new File([arrayBufferResult], filename, { type: mimeType })
  }

  const { mutateAsync: urlToFileMutation } = useMutation(urlToFile)

  const saveSignature = async (): Promise<void> => {
    setLoading()
    const imgDataUrl = signatureCanvasRef.current?.toDataURL() as string

    try {
      const urlToFileArgs = {
        url: imgDataUrl,
        filename: 'signature.png',
        mimeType: 'image/png',
      }
      const signatureAsFile = await urlToFileMutation(urlToFileArgs)
      if (!(signatureAsFile == null)) {
        const { fileUniqName, fileExtension } = getFileExtensionAndUniqName(urlToFileArgs.filename)

        const awsParams = {
          Body: signatureAsFile,
          Bucket: process.env.AWS_BUCKET_NAME!,
          Key: process.env.AWS_FILE_PATH! + fileUniqName,
        }

        const signatureFileAsObject: FileObj = {
          extension: fileExtension,
          file: process.env.AWS_PATH! + process.env.AWS_FILE_PATH! + fileUniqName,
          name: fileUniqName,
          size: signatureAsFile.size,
        }

        sendFileToAWS(awsParams, {
          onSuccess: () => {
            setLoaded()
            toast.success('Your signature is saved', {
              duration: 5000,
            })
          },
          onError: (e) => {
            if (!isOnline) {
              setLoaded()

              return
            }

            toast.error("Sorry, your signature didn't load", {
              duration: 5000,
            })
            console.log(e.message)
          },
        })

        field.onChange({
          ...signatureFileAsObject,
          dataUrl: imgDataUrl,
        })
      }
    } catch (error) {
      setError(name, { message: error.message })
    }
  }

  useEffect(() => {
    if (field.value !== undefined && 'dataUrl' in field.value) {
      signatureCanvasRef.current?.fromDataURL(field.value.dataUrl)
    }
  }, [])

  const errorMessageId = `${name}-helper-text`

  return (
    <SizingContainer className="signature-wrap" size={size}>
      <FormControl fullWidth error={error !== undefined}>
        <InputLabel sx={labelStyle}>{label}</InputLabel>
        <Box
          className="signature-canvas"
          sx={canvasWrapper}
          role="img"
          aria-label={label ?? 'Signature'}
          aria-invalid={error !== undefined}
          aria-describedby={error !== undefined ? errorMessageId : undefined}
        >
          <SignatureCanvas
            ref={signatureCanvasRef}
            clearOnResize={false}
            canvasProps={{
              style: {
                width: '100%',
                height: '100%',
              },
            }}
          />
        </Box>
        {error !== undefined && (
          <FormHelperText id={errorMessageId} role="alert">
            {error.message}
          </FormHelperText>
        )}
        <Box className="signature-btn-wrap" display="flex" justifyContent="flex-end">
          <Button
            className="signature-btn signature-btn__clear"
            onClick={clearCanvas}
            sx={cancelBtnStyle}
          >
            Clear
          </Button>
          <Button
            className="signature-btn signature-btn__save"
            sx={{
              ml: '8px',
            }}
            onClick={() => {
              void saveSignature()
            }}
            disabled={isLoading}
          >
            Save Signature
          </Button>
        </Box>
      </FormControl>
    </SizingContainer>
  )
}
